import { useEffect } from "react";
/**
 * useClickOutside hook
 *
 * Detects clicks outside a specified element and triggers a handler function.
 * Useful for dropdowns, modals, popovers, or any component that should close when clicking outside.
 *
 * @param {React.RefObject<HTMLElement>} ref - The ref of the element to detect outside clicks for
 * @param {(event: MouseEvent | TouchEvent) => void} handler - Function to call when a click occurs outside the element
 *
 * @example
 * const ref = useRef(null);
 * useClickOutside(ref, () => setIsOpen(false));
 */
export const useClickOutside = (ref, handler) => {
	useEffect(() => {
		const listener = (e) => {
			// If clicking inside the element ignore
			if (!ref.current || ref.current.contains(e.target)) return;
			handler(e);
		};

		document.addEventListener("mousedown", listener);
		document.addEventListener("touchstart", listener);

		return () => {
			document.removeEventListener("mousedown", listener);
			document.removeEventListener("touchstart", listener);
		};
	}, [ref, handler]);
};
